import React, { useEffect, useState, createContext, useContext } from 'react';
export interface WindowState {
  id: string;
  appId: string;
  title: string;
  isMinimized: boolean;
  isMaximized: boolean;
  position: {
    x: number;
    y: number;
  };
  size: {
    width: number;
    height: number;
  };
  zIndex: number;
}
interface WindowContextType {
  windows: WindowState[];
  openWindow: (appId: string, title: string) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  updateWindowPosition: (id: string, position: {
    x: number;
    y: number;
  }) => void;
  updateWindowSize: (id: string, size: {
    width: number;
    height: number;
  }) => void;
  isMobile: boolean;
}
const WindowContext = createContext<WindowContextType | undefined>(undefined);
export function WindowProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [windows, setWindows] = useState<WindowState[]>([]);
  const [nextZIndex, setNextZIndex] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const openWindow = (appId: string, title: string) => {
    const existingWindow = windows.find(w => w.appId === appId);
    if (existingWindow) {
      // Focus the window and restore if minimized
      focusWindow(existingWindow.id);
      if (existingWindow.isMinimized) {
        setWindows(prev => prev.map(w => w.id === existingWindow.id ? {
          ...w,
          isMinimized: false
        } : w));
      }
      return;
    }
    const newWindow: WindowState = {
      id: `${appId}-${Date.now()}`,
      appId,
      title,
      isMinimized: false,
      isMaximized: false,
      position: {
        x: 100 + windows.length * 30,
        y: 100 + windows.length * 30
      },
      size: {
        width: 800,
        height: 600
      },
      zIndex: nextZIndex
    };
    setWindows(prev => [...prev, newWindow]);
    setNextZIndex(prev => prev + 1);
  };
  const closeWindow = (id: string) => {
    setWindows(prev => prev.filter(w => w.id !== id));
  };
  const minimizeWindow = (id: string) => {
    const window = windows.find(w => w.id === id);
    if (!window) return;
    // Toggle minimize state
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      isMinimized: !w.isMinimized
    } : w));
    // If we're restoring (un-minimizing), focus the window
    if (window.isMinimized) {
      setNextZIndex(prev => prev + 1);
      setWindows(prev => prev.map(w => w.id === id ? {
        ...w,
        zIndex: nextZIndex
      } : w));
    }
  };
  const maximizeWindow = (id: string) => {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      isMaximized: !w.isMaximized
    } : w));
  };
  const focusWindow = (id: string) => {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      zIndex: nextZIndex
    } : w));
    setNextZIndex(prev => prev + 1);
  };
  const updateWindowPosition = (id: string, position: {
    x: number;
    y: number;
  }) => {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      position
    } : w));
  };
  const updateWindowSize = (id: string, size: {
    width: number;
    height: number;
  }) => {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      size
    } : w));
  };
  return <WindowContext.Provider value={{
    windows,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    focusWindow,
    updateWindowPosition,
    updateWindowSize,
    isMobile
  }} data-id="element-1019">
      {children}
    </WindowContext.Provider>;
}
export function useWindows() {
  const context = useContext(WindowContext);
  if (context === undefined) {
    throw new Error('useWindows must be used within a WindowProvider');
  }
  return context;
}